/**
 * Toast notification plugin wrapping vue3-toastify.
 *
 * Single source of truth for app-wide toasts. Components should prefer the
 * `useNotification()` composable; non-component code (store actions, utilities)
 * can call `showToast` directly.
 *
 * Usage:
 *   import { showToast } from '@/plugins/toast'
 *   showToast({ text: 'Saved!', tone: 'success' })
 *   showToast('A plain string message')
 */

import Vue3Toastify, { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';

// Map Profiteia tone names → vue3-toastify type names
const TONE_TO_TYPE = {
  success: 'success',
  danger: 'error',
  error: 'error',
  warning: 'warning',
  info: 'info',
  default: 'default',
};

/**
 * Return the current app theme ('dark' | 'light') by reading the CSS class
 * that themeManager.js toggles on <html>.  We read from the DOM at call-time
 * so dark-mode changes are immediately reflected without an extra reactive
 * subscription.
 */
function getTheme() {
  return typeof document !== 'undefined' &&
    (document.documentElement.classList.contains('dark') ||
      document.documentElement.dataset.mode === 'dark')
    ? 'dark'
    : 'light';
}

/**
 * Show a toast notification.
 *
 * @param {string|Object} payload
 *   A plain string, or an object with:
 *     - text {string}        The message to display
 *     - tone {string}        'success' | 'danger' | 'error' | 'warning' | 'info' (default: 'info')
 *     - durationMs {number}  Auto-close duration in ms (default: 3200, pass false to persist)
 *
 * @returns {string|number|null} The toast ID, or null if the message is empty.
 */
export function showToast(payload) {
  const normalized =
    typeof payload === 'string'
      ? { text: payload, tone: 'info', durationMs: 3200 }
      : {
          text: payload?.text || payload?.message || '',
          tone: payload?.tone || payload?.type || 'info',
          durationMs: payload?.durationMs ?? 3200,
        };

  if (!normalized.text) {
    return null;
  }

  const type = TONE_TO_TYPE[normalized.tone] || 'default';

  // Use a deterministic ID to prevent duplicate toasts (same text+tone visible
  // simultaneously).  Mirrors the deduplication logic previously in display.js.
  const toastId = `${normalized.tone}:${normalized.text}`;

  if (toast.isActive(toastId)) {
    return toastId;
  }

  return toast(normalized.text, {
    toastId,
    type,
    autoClose: normalized.durationMs,
    theme: getTheme(),
    position: 'bottom-right',
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
  });
}

/**
 * Vue plugin installer.
 * Registers vue3-toastify with sensible defaults that match the Profiteia
 * design language.  Register this plugin in main.js via app.use(toastPlugin).
 */
export const toastPlugin = {
  install(app, options = {}) {
    // Install the vue3-toastify plugin (not the toast function) so the
    // container receives valid Vue content and global defaults.
    app.use(Vue3Toastify, {
      autoClose: 3200,
      position: 'bottom-right',
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      multiple: true,
      maxToasts: 5,
      ...options,
    });
  },
};
